// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import FancyScroll, { FancyScrollRefProps } from "@synd/App/Components/Common/FancyScroll";
import ProductActions, { ProductActionsStyle } from "@synd/App/Components/Product/ProductActions";
import ProductBrand from "@synd/Overrides/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@synd/Overrides/Components/ProductDescription";
import ProductPartNumbers from "@synd/Overrides/Components/ProductPartNumbers";
import React, { createRef } from "react";
import { css } from "styled-components";

interface Props {
    // products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    products: ProductContextModel[];
    doSearch: () => void;
    canSeePrices?: boolean;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    // XNG-Changes
    header?: InjectableCss;
    headerLink?: LinkPresentationProps;
    wrapper?: InjectableCss;
    imageClickable?: ClickablePresentationProps;
    actionsGridItem?: InjectableCss;
    actions?: ProductActionsStyle;
    productPriceWrapper?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
    productTitle?: ProductDescriptionStyles;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 10px 0 5px 0;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
            background-color: lightgray;
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 65px;
            max-height: 50px;
            flex-basis: 65px;
        `,
    },
    image: {
        width: "50px",
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
            `,
        },
        css: css`
            width: 270px;
        `,
    },
    erpNumberText: {
        ellipsis: true,
        css: css`
            width: 270px;
            text-align: left;
            margin: 5px 0 0 0;
        `,
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;
    // XNG-Changes
    private productsColumn = React.createRef<HTMLDivElement>();
    private fancyScrollRef = createRef<FancyScrollRefProps>();

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);

        // XNG-Changes
        this.fancyScrollRef?.current?.updateScrollable();
    }

    render() {
        const { products, canSeePrices = false } = this.props;
        const styles = this.styles;

        return (
            <>
                <StyledWrapper {...styles.header}>
                    <Typography
                        {...styles.headerText}
                        data-test-selector="autocompleteProducts_Header"
                        className="autocomplete-products-header"
                    >
                        {translate("Products")}
                    </Typography>
                    {/** XNG-Changes */}
                    {products.length > 1 && (
                        <Link
                            onClick={() => {
                                this.props.doSearch();
                            }}
                            {...styles.headerLink}
                        >
                            {translate("View All")}
                        </Link>
                    )}
                </StyledWrapper>
                {/** XNG-Changes */}
                <FancyScroll refElement={this.productsColumn} ref={this.fancyScrollRef} shadowHeight="16px">
                    <StyledWrapper {...styles.wrapper} ref={this.productsColumn}>
                        {products.map(productContext => {
                            const {
                                product: {
                                    id,
                                    customerProductNumber,
                                    productNumber,
                                    manufacturerItem,
                                    productTitle,
                                    mediumImagePath,
                                    brand,
                                    packDescription,
                                },
                                productInfo: { productDetailPath },
                            } = productContext;

                            return (
                                <ProductContext.Provider value={productContext} key={id!}>
                                    <StyledWrapper
                                        {...(this.props.focusedItem?.id === id
                                            ? styles.focusedContainer
                                            : styles.container)}
                                        className="autocomplete-products-container"
                                    >
                                        <Clickable
                                            onClick={() => {
                                                this.props.goToUrl(productDetailPath);
                                            }}
                                            {...styles.imageClickable}
                                        >
                                            <LazyImage
                                                {...styles.image}
                                                src={mediumImagePath}
                                                className="autocomplete-products-image"
                                                // XNG-Changes
                                                altText={`autocomplete products for ${productTitle}`}
                                            />
                                        </Clickable>
                                        <StyledWrapper
                                            {...styles.infoGridItem}
                                            className="autocomplete-products-info-grid-item"
                                        >
                                            {brand && brand.name && <ProductBrand brand={brand} showLogo={false} />}
                                            {/* <Link
                                                {...styles.titleLink}
                                                data-test-selector={`productId_${id}`}
                                                onClick={() => {
                                                    this.props.goToUrl(productDetailPath);
                                                }}
                                                aria-label={productTitle}
                                            >
                                                {productNumber || customerProductNumber}
                                            </Link>
                                            {productTitle && (
                                                <Typography {...styles.productTitle}>{productTitle}</Typography>
                                            )} */}
                                            <ProductDescription
                                                product={productContext}
                                                extendedStyles={styles.productTitle}
                                            />
                                            <ProductPartNumbers
                                                showProductNumber={false}
                                                packSize={packDescription}
                                                productNumber={productNumber}
                                                // XNG-Changes
                                                // manufacturerItem={manufacturerItem}
                                                customerProductNumber={customerProductNumber}
                                            />
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.actionsGridItem}>
                                            {canSeePrices && (
                                                <StyledWrapper {...styles.productPriceWrapper}>
                                                    <VisuallyHidden
                                                        as="label"
                                                        htmlFor={`${productContext.productInfo.productId}-uom`}
                                                        id={`${productContext.productInfo.productId}-uom-label`}
                                                    >
                                                        {translate("U/M")}
                                                    </VisuallyHidden>
                                                    {/** TODO: Variant price range */}
                                                    <ProductPrice
                                                        showLabel={false}
                                                        product={productContext}
                                                        extendedStyles={styles.productPriceStyles}
                                                    />
                                                </StyledWrapper>
                                            )}

                                            {/** TODO: Variant disable qty select */}
                                            <ProductActions
                                                extendedStyles={styles.actions}
                                                hideUOM={true}
                                                quantityFieldVariant="select"
                                            />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </ProductContext.Provider>
                            );
                        })}
                    </StyledWrapper>
                </FancyScroll>
            </>
        );
    }
}

export default AutocompleteProducts;
